import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { ArrowLeft } from '@phosphor-icons/react';
import { Link } from '../../lib/routing';
import { useAuth } from '../../hooks/use-auth';
import { useExamImportJobs } from '../../hooks/use-exam-import-jobs';
import { useBulkImportJobs } from '../../hooks/use-bulk-import-jobs';
import { useSidemenuOpen } from './sidemenu-store';
import { useAppHeader } from './app-header-store';

export type AppHeaderProps = {
  content?: ReactNode;
  icon?: ReactNode;
  eyebrow?: string;
  eyebrowIcon?: ReactNode;
  backTo?: string;
  backLabel?: string;
  onBack?: () => void;
  actions?: ReactNode;
  width?: 'md' | 'lg' | 'xl' | 'full';
  showBurger?: boolean;
  /* 3 actions ou plus (ou libelles longs) : replie le bloc actions sur sa
     propre ligne des qu'il manque de place, plutot que d'ecraser le titre.
     Par defaut (false) le bloc reste sur la ligne du titre et se contente
     de retrecir, comme avant — n'active que pour les cas reellement denses,
     pour ne pas changer le rendu des pages a 0-2 actions. */
  denseActions?: boolean;
  /* Page publique/onboarding avec sa propre mise en page : masque la barre
     titre du shell plutot que de l'afficher vide. */
  hidden?: boolean;
};

const WIDTH: Record<NonNullable<AppHeaderProps['width']>, string> = {
  md: 'max-w-[880px]',
  lg: 'max-w-[1100px]',
  xl: 'max-w-[1400px]',
  full: 'max-w-none',
};

/* Hauteur reelle de la barre publiee comme variable CSS globale, pour les
   pages en layout plein ecran (scroll interne, pas de scroll document) qui
   doivent ceder exactement cette hauteur plutot que deviner une valeur fixe. */
export const APP_HEADER_HEIGHT_VAR = '--naia-app-header-h';

const ICON_BTN =
  'w-11 h-11 shrink-0 inline-flex items-center justify-center p-0 border-none bg-transparent rounded-[var(--naia-r-md)] text-muted-foreground no-underline cursor-pointer transition-colors hover:bg-accent hover:text-primary focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[var(--naia-blue)]';

/* Un job (exam-import ou bulk-import) est en cours → petite pastille
   persistante sur le burger, visible tiroir fermé, sous 900px (seuil du
   tiroir, header-rail.css). Au clic : ouvre le tiroir, où le détail
   (%/statut) est déjà affiché par les rail-items (header.tsx). Pas de
   second mécanisme d'affichage détaillé ici. */
function useHasRunningImportJob(): boolean {
  const { jobs: examJobs } = useExamImportJobs();
  const { jobs: bulkJobs } = useBulkImportJobs();
  return examJobs.some((j) => j.status === 'running') || bulkJobs.some((j) => j.status === 'running');
}

function Burger() {
  const { session, loading } = useAuth();
  const [open, setOpen] = useSidemenuOpen();
  const hasRunningJob = useHasRunningImportJob();
  if (loading || !session || open) return null;
  return (
    <button
      type="button"
      className="naia-burger relative w-11 h-11 shrink-0 flex flex-col items-center justify-center gap-[5px] p-0 bg-card border border-border rounded-xl shadow-card text-foreground cursor-pointer"
      onClick={() => setOpen(true)}
      aria-label="Ouvrir le menu"
      aria-expanded={false}
    >
      <span aria-hidden className="block w-[22px] h-[2.5px] rounded-sm bg-current" />
      <span aria-hidden className="block w-[22px] h-[2.5px] rounded-sm bg-current" />
      <span aria-hidden className="block w-[22px] h-[2.5px] rounded-sm bg-current" />
      {hasRunningJob && (
        <span
          aria-hidden
          className="absolute -top-1 -right-1 block size-[11px] rounded-full bg-naia-blue border-2 border-card min-[900px]:hidden"
        />
      )}
    </button>
  );
}

export default function AppHeader({
  content,
  icon,
  eyebrow,
  eyebrowIcon,
  backTo,
  backLabel,
  onBack,
  actions,
  width = 'lg',
  showBurger = false,
  denseActions = false,
}: AppHeaderProps) {
  const [scrolled, setScrolled] = useState(false);
  const headerRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useLayoutEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const publish = () => document.documentElement.style.setProperty(APP_HEADER_HEIGHT_VAR, `${el.offsetHeight}px`);
    publish();
    const ro = new ResizeObserver(publish);
    ro.observe(el);
    return () => {
      ro.disconnect();
      document.documentElement.style.setProperty(APP_HEADER_HEIGHT_VAR, '0px');
    };
  }, []);

  const backAria = backLabel ?? 'Retour';
  const backInner = (
    <>
      <ArrowLeft size={18} weight="bold" />
      {backLabel && <span className="hidden min-[900px]:inline text-[12.5px] font-bold">{backLabel}</span>}
    </>
  );
  const backClass = `${ICON_BTN} min-[900px]:w-auto min-[900px]:gap-1.5 min-[900px]:px-2.5`;
  const back = backTo ? (
    <Link to={backTo} className={backClass} aria-label={backAria}>{backInner}</Link>
  ) : onBack ? (
    <button type="button" onClick={onBack} className={backClass} aria-label={backAria}>{backInner}</button>
  ) : null;

  return (
    <header
      ref={headerRef}
      className={`sticky top-0 z-40 w-full bg-[color-mix(in_srgb,var(--naia-page)_88%,transparent)] backdrop-blur-md backdrop-saturate-150 border-b transition-[border-color,box-shadow] duration-150 ${
        scrolled ? 'border-border shadow-[0_6px_20px_-18px_rgba(11,42,74,0.55)]' : 'border-transparent'
      }`}
    >
      <div
        className={`${WIDTH[width]} mx-auto w-full flex items-start gap-3 pt-[max(10px,env(safe-area-inset-top))] pb-2.5 pl-[max(16px,env(safe-area-inset-left))] pr-[max(16px,env(safe-area-inset-right))] sm:pl-[max(24px,env(safe-area-inset-left))] sm:pr-[max(24px,env(safe-area-inset-right))] ${
          denseActions ? 'flex-wrap' : 'flex-wrap sm:flex-nowrap'
        }`}
      >
        <div className="flex items-center gap-1 shrink-0 min-h-11">
          {showBurger && <Burger />}
          {back}
        </div>

        <div className="flex-1 min-w-0 flex flex-col justify-center min-h-11">
          {eyebrow && (
            <span className="self-start inline-flex items-center gap-1.5 mb-[5px] px-2.5 py-1 rounded-full bg-accent text-primary text-[10.5px] font-extrabold uppercase tracking-[0.1em] whitespace-nowrap">
              {eyebrowIcon && <span aria-hidden className="flex items-center">{eyebrowIcon}</span>}
              {eyebrow}
            </span>
          )}

          <div className="flex items-center gap-2.5 min-w-0">
            {icon && <span aria-hidden className="hidden sm:flex shrink-0 text-primary">{icon}</span>}
            {content}
          </div>
        </div>

        {actions && (
          <div
            className={
              denseActions
                ? 'flex items-center flex-wrap justify-end gap-2 min-w-0 max-w-full ml-auto min-h-11 [&>*]:flex [&>*]:flex-wrap [&>*]:justify-end [&>*]:min-w-0 [&>*]:max-w-full'
                : 'flex items-center gap-2 shrink-0 ml-auto min-h-11 max-sm:basis-full max-sm:[&>*]:w-full max-sm:[&>*]:min-h-11'
            }
          >
            {actions}
          </div>
        )}
      </div>
    </header>
  );
}

export function HeaderContent({
  children,
  ...rest
}: { children: ReactNode } & Omit<AppHeaderProps, 'content'>) {
  useAppHeader({ ...rest, content: children });
  return null;
}

export const HEADER_TITLE_CLASS =
  'm-0 min-w-0 truncate font-extrabold leading-tight text-foreground text-[19px] sm:text-[24px]';

export const HEADER_BADGE_CLASS =
  'shrink-0 text-[11px] font-extrabold uppercase tracking-[0.05em] px-[9px] py-[3px] rounded-full whitespace-nowrap bg-accent text-primary';
